import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DataTable } from "components/molecules/table/table";
import { DataTableSkeleton, PageHeader, SectionCard, StatCard, StatusPill } from "components/admin";
import {
  appendCheckoutLineItem,
  createCheckoutLineItem,
  getCheckoutLineItemErrors,
  getCheckoutLineItemsErrorMessage,
  isCheckoutLineItemsValid,
  removeCheckoutLineItem,
  updateCheckoutLineItem,
  validateQuickOrderDraft,
} from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  BatchCheckoutForm,
  QuickOrderForm,
} from "pages/app/workspace/sections/forms/orders-section-forms";
import {
  SectionContentGrid,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, formatMoney, getApiErrorMessage } from "utils/admin";
import { OrdersSectionSection } from "pages/app/workspace/sections/styles/orders-section.inline.styles";
export const OrdersSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [merchandiseItemId, setMerchandiseItemId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [checkoutLineItems, setCheckoutLineItems] = useState([createCheckoutLineItem()]);
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["workspace-orders"],
    queryFn: () =>
      workspaceApi.orders.list({
        page: 1,
        per_page: 50,
      }),
  });
  const { data: merchandise = [] } = useQuery({
    queryKey: ["workspace-order-merchandise"],
    queryFn: () =>
      workspaceApi.merchandise.list({
        sort: "popular",
        per_page: 100,
      }),
  });
  const quickOrderErrors = validateQuickOrderDraft({
    merchandiseItemId,
    quantity,
  });
  const checkoutLineItemErrors = getCheckoutLineItemErrors(checkoutLineItems);
  const checkoutErrorMessage = getCheckoutLineItemsErrorMessage(checkoutLineItems);
  const createMutation = useMutation({
    mutationFn: () =>
      workspaceApi.orders.create({
        merchandise_item_id: merchandiseItemId,
        quantity,
        otp_required: false,
      }),
    onSuccess: () => {
      toast.success("Order placed successfully.");
      setMerchandiseItemId("");
      setQuantity(1);
      queryClient.invalidateQueries({
        queryKey: ["workspace-orders"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to place this order."));
    },
  });
  const checkoutMutation = useMutation({
    mutationFn: () =>
      workspaceApi.orders.checkout({
        line_items: checkoutLineItems,
      }),
    onSuccess: () => {
      toast.success("Checkout completed successfully.");
      setCheckoutLineItems([createCheckoutLineItem()]);
      queryClient.invalidateQueries({
        queryKey: ["workspace-orders"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to complete checkout."));
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (id: string | number) => workspaceApi.orders.cancel(id),
    onSuccess: () => {
      toast.success("Order cancelled.");
      queryClient.invalidateQueries({
        queryKey: ["workspace-orders"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to cancel this order."));
    },
  });
  const handleQuickOrderSubmit = () => {
    const firstError = quickOrderErrors.merchandise_item_id || quickOrderErrors.quantity;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    createMutation.mutate();
  };
  const handleCheckoutSubmit = () => {
    if (checkoutErrorMessage) {
      toast.error(checkoutErrorMessage);
      return;
    }
    checkoutMutation.mutate();
  };
  return (
    <>
      <PageHeader
        title="Orders"
        description="Create one-off orders, run batch checkout line items, and review order history tied to the current account."
      />

      <SectionStatsGrid>
        <StatCard label="Orders" value={orders.length} />
        <StatCard
          label="Paid"
          value={orders.filter((order: any) => order.status === "paid").length}
        />
        <StatCard
          label="Cancelled"
          value={orders.filter((order: any) => order.status === "cancelled").length}
        />
        <StatCard
          label="Spent"
          value={formatMoney(
            orders.reduce((sum: number, order: any) => sum + Number(order.total_price || 0), 0),
          )}
        />
      </SectionStatsGrid>

      <OrdersSectionSection>
        <SectionContentGrid>
          <SectionCard subtitle="Place a single merchandise order" title="Quick Order">
            <QuickOrderForm
              isSubmitting={createMutation.isPending}
              merchandiseError={quickOrderErrors.merchandise_item_id}
              merchandise={merchandise}
              onMerchandiseItemChange={setMerchandiseItemId}
              onQuantityChange={setQuantity}
              onSubmit={handleQuickOrderSubmit}
              quantityError={quickOrderErrors.quantity}
              quantity={quantity}
              selectedMerchandiseItemId={merchandiseItemId}
            />
          </SectionCard>

          <SectionCard
            subtitle="Use the checkout endpoint for multiple line items"
            title="Batch Checkout"
          >
            <BatchCheckoutForm
              formError={checkoutErrorMessage}
              isSubmitting={checkoutMutation.isPending}
              lineItems={checkoutLineItems}
              lineItemErrors={checkoutLineItemErrors}
              merchandise={merchandise}
              onAddLineItem={() =>
                setCheckoutLineItems((current) => appendCheckoutLineItem(current))
              }
              onLineItemChange={(index, patch) =>
                setCheckoutLineItems((current) => updateCheckoutLineItem(current, index, patch))
              }
              onRemoveLineItem={(index) =>
                setCheckoutLineItems((current) => removeCheckoutLineItem(current, index))
              }
              onSubmit={handleCheckoutSubmit}
              submitDisabled={
                checkoutMutation.isPending || !isCheckoutLineItemsValid(checkoutLineItems)
              }
            />
          </SectionCard>
        </SectionContentGrid>
      </OrdersSectionSection>

      <OrdersSectionSection>
        <DataTable
          canEdit={false}
          columns={[
            {
              label: "Item",
              accessor: (row: any) => row.merchandise_item?.name || "Unknown item",
            },
            {
              label: "Quantity",
              accessor: (row: any) => row.quantity ?? 0,
            },
            {
              label: "Total",
              accessor: (row: any) => row.formatted_total_price || formatMoney(row.total_price),
            },
            {
              label: "Status",
              accessor: (row: any) => <StatusPill value={row.status} />,
            },
            {
              label: "Placed",
              accessor: (row: any) => formatDateTime(row.created_at),
            },
          ]}
          data={orders}
          emptyDescription="Orders created through the API will appear here."
          emptyTitle="No orders yet"
          isLoading={isLoading}
          loadingState={<DataTableSkeleton columns={5} rows={5} />}
          onRemove={(id, row) => {
            const targetId = row?.id || id;
            if (row?.status === "cancelled") {
              return;
            }
            if (window.confirm("Cancel this order and issue a refund?")) {
              cancelMutation.mutate(String(targetId));
            }
          }}
        />
      </OrdersSectionSection>
    </>
  );
};
